import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  Loader2, 
  Check, 
  X, 
  CreditCard, 
  Clock, 
  Shield,
  ArrowLeft,
  AlertCircle,
  User,
  UserPlus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const CheckoutFlow = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get URL parameters
  const sessionId = searchParams.get('session_id');
  const planKey = searchParams.get('plan');
  const billingCycle = searchParams.get('billing');
  const couponCode = searchParams.get('coupon');
  const status = searchParams.get('status'); // 'success', 'cancel', or null for checkout
  
  // Component states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, false = not auth, true = auth
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Handle success/cancel status from URL
  useEffect(() => {
    if (status === 'success' && sessionId) {
      handleCheckoutSuccess();
    } else if (status === 'cancel') {
      setError('Checkout was cancelled. You can try again anytime.');
    }
  }, [status, sessionId]);

  // Check authentication status and fetch plan data
  useEffect(() => {
    checkAuthenticationAndLoadPlan();
  }, [planKey]);

  const checkAuthenticationAndLoadPlan = async () => {
    try {
      // Check if user is authenticated
      const authResponse = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      setIsAuthenticated(authResponse.ok);
    } catch (error) {
      console.error('Error checking authentication:', error);
      setIsAuthenticated(false);
    }
    
    // Fetch plan data regardless of authentication status
    if (planKey) {
      await fetchPlanData();
    }
  };

  const fetchPlanData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/plans`);
      
      if (response.ok) {
        const data = await response.json();
        const plan = data.plans?.find(p => p.key === planKey);
        if (plan) {
          setSelectedPlan(plan);
        } else {
          setError('Selected plan not found. Please go back and select a valid plan.');
        }
      } else {
        setError('Failed to load plan information.');
      }
    } catch (error) {
      console.error('Error fetching plan data:', error);
      setError('Failed to load plan information.');
    }
  };

  // Create checkout session and redirect to Stripe (only when authenticated)
  const createCheckoutSession = async () => {
    if (!planKey) {
      setError('No plan selected. Please go back and select a plan.');
      return;
    }

    if (!isAuthenticated) {
      setError('Please log in or sign up to continue with checkout.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const requestData = {
        plan_key: planKey,
        billing_cycle: billingCycle || 'monthly',
        success_url: `${window.location.origin}/checkout?status=success&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/checkout?status=cancel`
      };

      // Add coupon if provided
      if (couponCode) {
        requestData.coupon_code = couponCode;
      }

      const response = await fetch(`${BACKEND_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Redirect to login with current URL as redirect
          navigate(`/auth/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
          return;
        }
        throw new Error(data.detail || `Failed to create checkout session: ${response.status}`);
      }

      // Redirect to Stripe checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout process');
    } finally {
      setLoading(false);
    }
  };

  // Handle successful checkout completion
  const handleCheckoutSuccess = async () => {
    setLoading(true);

    try {
      console.log('Handling checkout success with session_id:', sessionId);
      
      // For test environment, manually activate subscription since webhooks don't work locally
      if (sessionId) {
        console.log('Attempting subscription activation using session ID...');
        
        const activationResponse = await fetch(`${BACKEND_URL}/api/checkout/activate-subscription`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ 
            session_id: sessionId
          })
        });

        if (activationResponse.ok) {
          const activationData = await activationResponse.json();
          console.log('Subscription activation successful:', activationData);
          
          // Fetch updated subscription data
          const refreshResponse = await fetch(`${BACKEND_URL}/api/user/subscription`, {
            credentials: 'include'
          });
          
          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            setSubscription(refreshData);
            console.log('Subscription data refreshed:', refreshData);
          }
        } else {
          const errorData = await activationResponse.json();
          console.error('Subscription activation failed:', errorData);
          setError('Payment completed successfully. Please contact support if your subscription is not activated shortly.');
        }
      } else {
        console.log('No session ID available for activation');
        setError('Payment completed successfully. Your subscription will be activated shortly.');
      }

      // Brief delay to show success message
      setTimeout(() => {
        setLoading(false);
      }, 2000);

    } catch (err) {
      console.error('Error fetching subscription:', err);
      setLoading(false);
    }
  };

  // Navigate back to pricing
  const goBackToPricing = () => {
    navigate('/#pricing');
  };

  // Navigate to billing settings
  const goToBillingSettings = () => {
    navigate('/app/settings?tab=billing');
  };

  // Auto-start checkout if plan is provided, no status, and user is authenticated
  useEffect(() => {
    if (planKey && !status && !loading && !checkoutUrl && isAuthenticated === true) {
      // Don't auto-start, let user review and click "Continue to Payment"
      // createCheckoutSession();
    }
  }, [planKey, status, loading, checkoutUrl, isAuthenticated]);

  // Render success state
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-6 dark:from-green-950/20 dark:to-blue-950/20">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center dark:bg-gray-900">
          {loading ? (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-green-900/30">
                <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-100">
                Activating Your Subscription
              </h1>
              <p className="text-gray-600 mb-6 dark:text-gray-300">
                Please wait while we set up your account...
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-green-900/30">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-100">
                Welcome to SaaS-Base!
              </h1>
              <p className="text-gray-600 mb-6 dark:text-gray-300">
                Your subscription has been activated successfully. 
                {subscription?.subscription?.trial_end && (
                  <span className="block mt-2 text-sm">
                    Your 14-day free trial has started!
                  </span>
                )}
              </p>
              
              {subscription?.plan && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6 dark:bg-gray-800">
                  <h3 className="font-semibold text-gray-900 mb-2 dark:text-gray-100">
                    {subscription.plan.name} Plan
                  </h3>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    <p>Voice Minutes: {subscription.allocations?.voice_minutes === -1 ? 'Unlimited' : subscription.allocations?.voice_minutes || 0}</p>
                    <p>SMS Messages: {subscription.allocations?.sms_count === -1 ? 'Unlimited' : subscription.allocations?.sms_count || 0}</p>
                    <p>Storage: {subscription.allocations?.storage_gb === -1 ? 'Unlimited' : `${subscription.allocations?.storage_gb || 0}GB`}</p>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <Button onClick={() => navigate('/app')} className="w-full" size="lg">
                  Start Using SaaS-Base
                </Button>
                <Button 
                  onClick={goToBillingSettings}
                  variant="outline" 
                  className="w-full"
                >
                  Manage Subscription
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Render cancel state
  if (status === 'cancel') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-6 dark:from-red-950/20 dark:to-orange-950/20">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center dark:bg-gray-900">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-red-900/30">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-100">
            Checkout Cancelled
          </h1>
          <p className="text-gray-600 mb-6 dark:text-gray-300">
            No worries! You can try again anytime. Your account remains active with the free plan.
          </p>
          
          <div className="space-y-3">
            <Button onClick={goBackToPricing} className="w-full" size="lg">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Pricing
            </Button>
            <Button onClick={() => navigate('/app')} variant="outline" className="w-full">
              Continue with Free Plan
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render checkout initiation state
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center p-6 dark:from-indigo-950/20 dark:to-purple-950/20">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center dark:bg-gray-900">
        {/* Loading state */}
        {(loading || isAuthenticated === null) ? (
          <>
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-indigo-900/30">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-100">
              {isAuthenticated === null ? 'Loading...' : 'Preparing Your Checkout'}
            </h1>
            <p className="text-gray-600 mb-4 dark:text-gray-300">
              {isAuthenticated === null ? 'Checking authentication...' : 'Redirecting you to secure payment...'}
            </p>
          </>
        ) : error ? (
          /* Error state */
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-red-900/30">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-100">
              Checkout Error
            </h1>
            <p className="text-red-600 mb-6 text-sm dark:text-red-300">
              {error}
            </p>
            <div className="space-y-3">
              <Button onClick={() => setError('')} className="w-full" size="lg">
                Try Again
              </Button>
              <Button onClick={goBackToPricing} variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Pricing
              </Button>
            </div>
          </>
        ) : !isAuthenticated ? (
          /* Authentication required state */
          <>
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-blue-900/30">
              <User className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-100">
              Almost There!
            </h1>
            <p className="text-gray-600 mb-6 dark:text-gray-300">
              Please log in or create an account to continue with your {selectedPlan?.name || planKey} plan subscription.
            </p>

            {/* Plan Summary */}
            {selectedPlan && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 dark:bg-gray-800">
                <h3 className="font-semibold text-gray-900 mb-2 dark:text-gray-100">{selectedPlan.name}</h3>
                
                {/* Trial Pricing Breakdown */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Total due today:</span>
                    <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">US$0.00</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-green-600 font-medium flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      14 Days Free
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-600">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Total after trial:</span>
                    <span className="text-lg font-semibold text-indigo-600">
                      US${selectedPlan.pricing?.[billingCycle || 'monthly'] || 0}.00
                      <span className="text-sm font-normal text-gray-500">/{billingCycle || 'monthly'}</span>
                    </span>
                  </div>
                  
                  {couponCode && (
                    <div className="flex justify-between items-center pt-1">
                      <span className="text-sm text-green-600">Coupon applied:</span>
                      <span className="text-sm font-medium text-green-600">{couponCode}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Link to={`/auth/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}>
                <Button className="w-full" size="lg">
                  <User className="w-4 h-4 mr-2" />
                  Log In to Continue
                </Button>
              </Link>
              <Link to={`/auth/signup?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}>
                <Button variant="outline" className="w-full" size="lg">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Account
                </Button>
              </Link>
              <Button onClick={goBackToPricing} variant="ghost" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Pricing
              </Button>
            </div>
          </>
        ) : (
          /* Authenticated checkout state */
          <>
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-indigo-900/30">
              <CreditCard className="w-8 h-8 text-indigo-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-100">
              Complete Your Purchase
            </h1>
            <p className="text-gray-600 mb-6 dark:text-gray-300">
              Review your selection and continue to secure payment.
            </p>

            {/* Plan Summary */}
            {selectedPlan && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left dark:bg-gray-800">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{selectedPlan.name}</h3>
                  {selectedPlan.is_most_popular && (
                    <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full dark:bg-purple-900/30 dark:text-purple-300">
                      Most Popular
                    </span>
                  )}
                </div>
                
                {/* Trial Pricing Breakdown */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Plan:</span>
                    <span className="font-medium">{selectedPlan.name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Billing:</span>
                    <span className="font-medium capitalize">{billingCycle || 'monthly'}</span>
                  </div>
                  
                  <div className="border-t pt-3 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-300">Total due today:</span>
                      <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">US$0.00</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-600 font-medium flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        Trial period:
                      </span>
                      <span className="text-sm font-medium text-green-600">14 days free</span>
                    </div>
                    
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-600">
                      <span className="text-sm text-gray-600 dark:text-gray-300">Total after trial:</span>
                      <span className="text-lg font-bold text-indigo-600">
                        US${selectedPlan.pricing?.[billingCycle || 'monthly'] || 0}.00
                        <span className="text-sm font-normal text-gray-500">/{billingCycle || 'monthly'}</span>
                      </span>
                    </div>
                    
                    {couponCode && (
                      <div className="flex justify-between items-center pt-1">
                        <span className="text-sm text-green-600">Coupon applied:</span>
                        <span className="text-sm font-medium text-green-600">{couponCode}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-center text-sm text-gray-500 mb-6 dark:text-gray-400">
              <Shield className="w-4 h-4 mr-2" />
              Secured by Stripe
            </div>

            <div className="space-y-3">
              <Button onClick={createCheckoutSession} className="w-full" size="lg" disabled={loading}>
                <CreditCard className="w-4 h-4 mr-2" />
                Continue to Payment
              </Button>
              <Button onClick={goBackToPricing} variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Pricing
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutFlow;